// Code-based TanStack Router tree (file names follow spec 13's `routes/{…}` layout).

import { ROUTE_RENDERED_EVENT } from "@companyos/ui";
import { QueryClientProvider } from "@tanstack/react-query";
import { createRouter, type RouterHistory, RouterProvider } from "@tanstack/react-router";
import { watchMfaRequired, watchSessionExpiry } from "./api/session-expiry";
import type { RouterContext } from "./routes/__root";
import { rootRoute } from "./routes/__root";
import { authedRoute } from "./routes/_authed";
import { accountRoute } from "./routes/account";
import { companyIndexRoute, companyRoute } from "./routes/c.$slug";
import { agentsRoute, agentsRoutes, teamConnectionsRoute } from "./routes/c.$slug.agents";
import { briefReportRoute } from "./routes/c.$slug.briefs";
import { calendarRoute } from "./routes/c.$slug.calendar";
import {
  chatArchivedRoute,
  chatBrowseRoute,
  chatChannelRoute,
  chatIndexRoute,
  chatMessageRoute,
  chatPeopleRoute,
  chatRoute,
  chatSavedRoute,
  chatSearchRoute,
  chatThreadsRoute,
  chatTopicRoute,
  chatUnreadsRoute,
} from "./routes/c.$slug.chat";
import { flowRoute } from "./routes/c.$slug.flows";
import { kpisDeRoute, kpisIndexRoute, kpisRoute, scorecardRoute } from "./routes/c.$slug.kpis";
import { deKpisRoute } from "./routes/c.$slug.kpis.agents";
import {
  chartEditorRoute,
  chartIndexRoute,
  chartRoute,
  chartSeatRoute,
} from "./routes/c.$slug.kpis.chart";
import { hoursEntryRoute } from "./routes/c.$slug.kpis.entry.hours";
import { numbersEntryRoute } from "./routes/c.$slug.kpis.entry.numbers";
import { importRoute } from "./routes/c.$slug.kpis.import";
import { issuesRoute } from "./routes/c.$slug.kpis.issues";
import { meetingRoute, meetingsRoute } from "./routes/c.$slug.kpis.meetings";
import { myNumbersRoute, myScorecardRoute } from "./routes/c.$slug.kpis.mine";
import { prioritiesRoute } from "./routes/c.$slug.kpis.priorities";
import { publicationsRoute } from "./routes/c.$slug.kpis.publications";
import { visionRoute } from "./routes/c.$slug.kpis.vision";
import {
  legacyCadenceTree,
  legacyChartTree,
  legacyHomeTree,
  legacyReportsTree,
  legacyScorecardTree,
  legacyScreensTree,
  legacySetupDeRoute,
  legacySetupDesRoute,
  legacySetupEntryRoutes,
} from "./routes/c.$slug.legacy";
import { portfolioRoute, portfolioRoutes } from "./routes/c.$slug.portfolio";
import {
  reportLiveRoute,
  reportsIndexRoute,
  reportsRoute,
  reportUnsubscribeRoute,
  reportWeekRoute,
} from "./routes/c.$slug.reports";
import { reportsFinanceRoutes } from "./routes/c.$slug.reports.finance";
import { screensRoute } from "./routes/c.$slug.screens";
import { settingsRoute } from "./routes/c.$slug.settings";
import { setupRoute, unknownModeRoute } from "./routes/c.$slug.setup";
import { setupAgentDirectionRoute } from "./routes/c.$slug.setup.agent-direction";
import { setupDeployAgentRoute } from "./routes/c.$slug.setup.agents";
import { setupAssessmentsRoute } from "./routes/c.$slug.setup.assessments";
import { setupBreakGlassRoute } from "./routes/c.$slug.setup.break-glass";
import { setupChartRoute } from "./routes/c.$slug.setup.chart";
import { setupChiefOfStaffRoute } from "./routes/c.$slug.setup.chief-of-staff";
import { setupClaudeCodeRoute } from "./routes/c.$slug.setup.claude-code";
import { setupCollectionRoute } from "./routes/c.$slug.setup.collection";
import { setupImportRoute } from "./routes/c.$slug.setup.data-import";
import { setupDecisionsRoute } from "./routes/c.$slug.setup.decisions";
import { setupDeOverridesRoute, setupDeploysRoute } from "./routes/c.$slug.setup.deploys";
import { setupDeNewRoute } from "./routes/c.$slug.setup.des";
import { setupFinanceRoutes } from "./routes/c.$slug.setup.finance";
import { setupGeneralRoute } from "./routes/c.$slug.setup.general";
import { setupKpiReportingRoute } from "./routes/c.$slug.setup.kpi-reporting";
import { setupKpiVisibilityRoute } from "./routes/c.$slug.setup.kpi-visibility";
import { setupMemoryRoute } from "./routes/c.$slug.setup.memory";
import { setupMemoryPrivacyRoute } from "./routes/c.$slug.setup.memory-privacy";
import { setupMetricsRoute } from "./routes/c.$slug.setup.metrics";
import { setupModelPolicyRoute } from "./routes/c.$slug.setup.model-policy";
import { setupPeopleRoute, setupRolesRoute } from "./routes/c.$slug.setup.people";
import { setupRecallRoute } from "./routes/c.$slug.setup.recall";
import { setupSeatsRoute } from "./routes/c.$slug.setup.seats";
import { setupSubscriptionsRoute } from "./routes/c.$slug.setup.subscriptions";
import { setupTagsRoute } from "./routes/c.$slug.setup.tags";
import { setupTeamsRoute } from "./routes/c.$slug.setup.teams";
import { setupTemplatesRoute } from "./routes/c.$slug.setup.templates";
import { setupVoiceRoute } from "./routes/c.$slug.setup.voice";
import { setupWebhooksRoute } from "./routes/c.$slug.setup.webhooks";
import { setupWorkforceRoutes } from "./routes/c.$slug.setup.workforce";
import {
  workBoardRoute,
  workBoardsRoute,
  workDeskRoute,
  workDocsRoute,
  workHomeRoute,
  workIndexRoute,
  workMemoryReviewRoute,
  workMyRoute,
  workPageRoute,
  workRoute,
  workSearchRoute,
  workSpaceRoute,
} from "./routes/c.$slug.work";
import { workAutomationsRoute } from "./routes/c.$slug.work.automations";
import { workContactsRoute } from "./routes/c.$slug.work.contacts";
import { workTaskPageRoute } from "./routes/c.$slug.work.t.$id";
import { createGalleryRoute } from "./routes/dev.gallery";
import { inboxRoute } from "./routes/inbox";
import { indexRoute } from "./routes/index";
import { invitationAcceptRoute } from "./routes/invitations.accept";
import { loginRoute } from "./routes/login";
import { notificationsRoute } from "./routes/notifications";
import { onboardingIntakeRoute } from "./routes/onboarding-intake";
import { operatorRoute } from "./routes/operator";
import { agencyOperatorRoutes } from "./routes/operator.agency";
import { deployOperatorRoutes } from "./routes/operator.deploys";
import { operatorGateRoute } from "./routes/operator.gate";
import { templateOperatorRoutes } from "./routes/operator.templates";
// Operations (Home, add-ons, costs, failed events, the operator DE list): one block, appended.
import { opsCompanyRoutes, opsOperatorRoutes, opsSetupRoutes } from "./routes/ops-routes";
import { resetPasswordRoute } from "./routes/reset-password";
import { respondRoute } from "./routes/respond";
import { screenWindowRoute } from "./routes/screens.$lease";
import {
  legacySetupAppearanceRoute,
  settingsAppearanceRoute,
  settingsNotificationsRoute,
} from "./routes/settings.appearance";
// BYO-01: My subscriptions, Setup › Keys & connections, the OAuth callback.
import {
  connectCallbackRoute,
  mySubscriptionsRoute,
  setupConnectionChecksRoute,
  setupConnectionsRoute,
} from "./routes/subscriptions-routes";
import { superAdminClaimRoute } from "./routes/super-admin.claim";
import { surveyRoute } from "./routes/survey";

// The primitives gallery exists in dev builds only: the constant folds to `[]` in production and
// the gallery module, which has no top-level side effects, is dropped from the bundle.
const devRoutes = import.meta.env.DEV ? [createGalleryRoute()] : [];

export const routeTree = rootRoute.addChildren([
  ...devRoutes,
  loginRoute,
  resetPasswordRoute,
  invitationAcceptRoute,
  // SUPER-ADMIN: the host command's single-use claim link (public; the token is in the fragment)
  superAdminClaimRoute,
  respondRoute,
  surveyRoute,
  // ONBOARDING-P1: the client owner's intake by its link (public)
  onboardingIntakeRoute,
  authedRoute.addChildren([
    indexRoute,
    accountRoute,
    // SCREENS-VIEWER: one live session in its own window (no app chrome).
    screenWindowRoute,
    // SETUP-REGROUP: Your settings › Appearance (was Setup › Appearance).
    settingsAppearanceRoute,
    settingsNotificationsRoute,
    operatorRoute,
    operatorGateRoute,
    connectCallbackRoute,
    ...opsOperatorRoutes,
    // DC-03: Operator › Templates (the catalog, a template, a version).
    ...templateOperatorRoutes,
    // DC-04: Operator › Deploys (the list, a deploy).
    ...deployOperatorRoutes,
    // M5-05: Operator › Approvals SLA, Supervisor load, Cost to serve, Break-glass.
    ...agencyOperatorRoutes,
    companyRoute.addChildren([
      companyIndexRoute,
      // The five modes (decision 2026-10-19; KPIS-MODE 2026-10-22), in the mode bar's order.
      chatRoute.addChildren([
        chatIndexRoute,
        chatBrowseRoute,
        chatUnreadsRoute,
        chatThreadsRoute,
        chatPeopleRoute,
        chatArchivedRoute,
        chatSavedRoute,
        chatSearchRoute,
        chatMessageRoute,
        chatChannelRoute,
        chatTopicRoute,
      ]),
      workRoute.addChildren([
        workIndexRoute,
        workBoardsRoute,
        // SHELL-DECISIONS: `/work/home` and `/work/desk` → `/work/boards`.
        workHomeRoute,
        workDeskRoute,
        workMyRoute,
        workSpaceRoute,
        workBoardRoute,
        workPageRoute,
        // Parity P1: a task as a full page
        workTaskPageRoute,
        // DOCS-KB
        workDocsRoute,
        // M7-04
        workMemoryReviewRoute,
        // Parity P5
        workAutomationsRoute,
        // INC2-CONTACT
        workContactsRoute,
        // T5-TASK-SEARCH
        workSearchRoute,
      ]),
      // KPIS-MODE: Chart and Cadence as one mode.
      kpisRoute.addChildren([
        kpisIndexRoute,
        chartRoute.addChildren([chartIndexRoute, chartSeatRoute, chartEditorRoute]),
        scorecardRoute,
        kpisDeRoute,
        myNumbersRoute,
        myScorecardRoute,
        // PRIORITIES-HOME: the quarter's priorities.
        prioritiesRoute,
        // Structure: published scorecards.
        publicationsRoute,
        // Data entry (managers).
        hoursEntryRoute,
        numbersEntryRoute,
        importRoute,
        // MEETINGS: issues, the team meetings, one meeting, the vision page.
        issuesRoute,
        meetingsRoute,
        meetingRoute,
        visionRoute,
        // DE-KPIS-LIVE: every DE's runs, KPIs and cost, its helpers under it.
        deKpisRoute,
        // NAV-REDESIGN: Reports is KPIs › Reports. M5-04 Finance (moved here from Chart,
        // KPIS-MODE). Static paths first: `finance` before `$week`.
        reportsRoute.addChildren([
          reportsIndexRoute,
          ...reportsFinanceRoutes,
          // REPORTS-UI: the report email's opt-out landing (static, before `$week`).
          reportUnsubscribeRoute,
          // FOUNDER-OS phase 1: Reports › Live in Founder OS (static, before `$week`).
          reportLiveRoute,
          reportWeekRoute,
        ]),
      ]),
      // M3-08: roster, DE profile, runs, approvals, wake-ups, budgets; M4-01: My agent; M4-02:
      // skill packs.
      agentsRoute.addChildren(agentsRoutes),
      // CONNECTIONS-AREA: `/team/connections` opens Team › Connections.
      teamConnectionsRoute,
      // CALENDAR-C1: the native calendar (one page; view and day in the search).
      calendarRoute,
      // GUIDED-FLOW: a guided flow as a full-screen panel.
      flowRoute,
      // COS-BRIEF-RULES: a report sent to you, in full (its inbox item's page).
      briefReportRoute,
      // SCREENS-S1: the company's computer, its agents' live screens and logins (NAV-REDESIGN:
      // the rail's Live, `/live`).
      screensRoute,
      // FOUNDER-OS: the Portfolio mode (Founder OS only).
      portfolioRoute.addChildren(portfolioRoutes),
      // Old URLs (Scorecard, Chart, Cadence, Setup › DEs and data entry) redirect to their new homes.
      legacyScorecardTree,
      legacyChartTree,
      legacyCadenceTree,
      // NAV-REDESIGN: `/reports/…` → `/kpis/reports/…`, `/screens` → `/live`.
      legacyReportsTree,
      legacyScreensTree,
      // SHELL-DECISIONS: `/home[/…]` → `/desk[/…]` (Home was renamed Desk).
      legacyHomeTree,
      setupRoute.addChildren([
        settingsRoute,
        legacySetupDesRoute,
        legacySetupDeRoute,
        ...legacySetupEntryRoutes,
        // UI access
        setupGeneralRoute,
        setupPeopleRoute,
        setupRolesRoute,
        // KPI-VISIBILITY: who sees whose KPI data.
        setupKpiVisibilityRoute,
        legacySetupAppearanceRoute,
        // UI des: DE create wizard, DE subscriptions (the DE list and profiles are in Agents)
        setupDeNewRoute,
        setupSubscriptionsRoute,
        // DC-05: the company side of DC-04's deploy approval and template override.
        setupDeploysRoute,
        setupDeOverridesRoute,
        // DC-11: `/setup/agents/deploy` redirects to Add digital employee (DE-WIZARD).
        setupDeployAgentRoute,
        // UX pass 1.1: Budgets and Skill packs, moved from the Workforce sidebar.
        ...setupWorkforceRoutes,
        // Structure: seats, metrics and targets.
        setupSeatsRoute,
        setupMetricsRoute,
        // AGENT-REPORTING: reporting duties, KPI scopes and review flags.
        setupKpiReportingRoute,
        // M8-ASSESS: assessment rounds, their reports and proposed chart.
        setupAssessmentsRoute,
        // COLLECT-FLOWS: collection sessions per person and what each role waits for.
        setupCollectionRoute,
        // M1-08: teams.
        setupTeamsRoute,
        // CHART-DIMENSIONS: the chart's default dimensions and who sees each.
        setupChartRoute,
        // M1-07: outbound webhooks.
        setupWebhooksRoute,
        // WOS-BUNDLE: the company's tags (rename, recolour, merge, delete).
        setupTagsRoute,
        // IMPORT (HARDEN): guided Zulip / ClickUp imports.
        setupImportRoute,
        // M7-04: Recall (the `memory` add-on).
        setupRecallRoute,
        // RECALL-RETENTION-HOLD-FORGET: retention, holds, pins, forgets, audit.
        setupMemoryPrivacyRoute,
        // RECALL-ADMIN-MEMORY: Setup › Memory (review, browse, spaces, rules, health).
        setupMemoryRoute,
        // M1-06: templates (preview and apply).
        setupTemplatesRoute,
        // UI ops: costs, add-ons & billing.
        ...opsSetupRoutes,
        // BYO-01: subscription paths, company keys and connections.
        setupConnectionsRoute,
        setupConnectionChecksRoute,
        // M5-04: Setup › Finance.
        ...setupFinanceRoutes,
        // M5-05: the company's break-glass log and decisions.
        setupBreakGlassRoute,
        // MODEL-POLICY: allowed models, defaults per task kind, own AI keys, agent overrides.
        setupModelPolicyRoute,
        // B5-DECIDE: the classifier switches and the DEs' rubrics.
        setupDecisionsRoute,
        // COS-SLICE1: the Chief of Staff's loop and each DE's digest.
        setupChiefOfStaffRoute,
        // COS-MODES: a person's own card "Who may direct my agents" (every member).
        setupAgentDirectionRoute,
        setupVoiceRoute,
        // WF-CONTROL: the Workforce Control add-on and the caller's Claude Code connections.
        setupClaudeCodeRoute,
      ]),
      ...opsCompanyRoutes,
      mySubscriptionsRoute,
      // SEARCH-NOTIF: account menu › Notifications.
      notificationsRoute,
      // INBOX-READABLE: the inbox drawer's "Open as page".
      inboxRoute,
      unknownModeRoute,
    ]),
  ]),
]);

function buildRouter(context: RouterContext, history?: RouterHistory) {
  return createRouter({
    routeTree,
    context,
    defaultPreload: "intent",
    scrollRestoration: true,
    ...(history ? { history } : {}),
  });
}

export type AppRouter = ReturnType<typeof buildRouter>;

export function createAppRouter(context: RouterContext, history?: RouterHistory): AppRouter {
  const router = buildRouter(context, history);
  // CHAT-AUTOSCROLL: the router's scroll restoration may move a message list after the route
  // renders; the lists put themselves back at the newest message (use-stick-to-bottom.ts).
  router.subscribe("onRendered", () => window.dispatchEvent(new Event(ROUTE_RENDERED_EVENT)));
  // Central 401 handling: drop the cached session and go to /login, then back here.
  watchSessionExpiry(context.queryClient, (hadSession) => {
    const { pathname, href } = router.state.location;
    // Public pages handle a missing session themselves.
    if (
      pathname === "/login" ||
      pathname === "/invitations/accept" ||
      pathname === "/super-admin/claim"
    )
      return;
    if (
      pathname.startsWith("/respond/") ||
      pathname.startsWith("/survey/") ||
      pathname.startsWith("/onboarding/")
    )
      return;
    // `expired` makes the sign-in page say why the user is there (coverage matrix §2.9).
    void router.navigate({
      to: "/login",
      search: { redirect: href, ...(hadSession ? { expired: true } : {}) },
      replace: true,
    });
  });
  // HARDEN-mfa: a 403 `mfa_enrollment_required` anywhere rereads `/v1/me`; its `mfa` then puts
  // the two-step enrolment screen in front of the app (features/account/mfa.tsx `MfaGate`).
  watchMfaRequired(context.queryClient);
  return router;
}

declare module "@tanstack/react-router" {
  interface Register {
    router: AppRouter;
  }
}

export function App({ router, context }: { router: AppRouter; context: RouterContext }) {
  return (
    <QueryClientProvider client={context.queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
